import {
  UNIFIED_SOURCE, CLUSTER_IMG_SOURCE, GEOFENCE_SOURCE, TRAIL_SOURCE,
  HEATMAP_INCIDENTS_SOURCE, JURISDICTION_SOURCE, JURISDICTION_MASK_SOURCE,
  LAYERS, DOM_MARKER_MIN_ZOOM, LABEL_TEXT_SIZE_PX,
} from '../utils/map-constants.js'
import { buildIconExpression } from '../utils/icon-registry.js'
import { EDOMEX_BOUNDARY_GEOJSON, EDOMEX_MASK_GEOJSON } from '../data/edomex-boundary.js'

export function addAllLayers(map) {
  // La frontera del EdoMex va PRIMERO (más abajo en el z-order) para que
  // las geocercas queden encima y no se les tape el color que viene del
  // backend. Ver nota en addJurisdictionBoundary.
  addJurisdictionBoundary(map)
  addGeofenceLayers(map)
  // El heatmap va DEBAJO de los clusters e iconos. De lo contrario el
  // gradiente rojo tapaba las tarjetas de cluster y los markers cuando
  // coincidian con zonas densas de incidentes.
  addHeatmapLayer(map)
  addClusterLayers(map)
  // Los rastros van DEBAJO de halos e iconos. Antes se agregaban al final de
  // esta función y por lo tanto encima de todo: el rastro de la unidad que se
  // está siguiendo cruzaba por encima de su propio marker y de su etiqueta.
  // El rastro es contexto —de dónde viene—, el marker es el dato: dónde está
  // ahora. Mismo criterio que el rastro histórico del reportante y que el halo
  // de selección.
  addTrailLayers(map)
  // Halos van DEBAJO de los iconos (outer más abajo, inner encima, icono
  // arriba de todo). MARKERS-DESIGN-SPEC.md §9: orden vertical "halo
  // externo → halo interno → marker base → ring de selected".
  addHaloLayers(map)
  // Punto pequeño en el centro real del grupo cuando el spread radial
  // está activo. Va DESPUÉS de los halos para quedar arriba del halo
  // (que tinta el área), y ANTES de los iconos para que un marker
  // accidentalmente centrado lo tape (caso edge raro). Filtra por
  // properties.spreadActive=true; sin spread, la layer está vacía.
  addSpreadCenterLayer(map)
  // Placa de ámbito de cámaras (estatal/municipal/otro) — va ANTES de los
  // iconos estáticos para dibujarse debajo: el marker de la cámara la tapa
  // al centro y solo queda visible el borde de color de ~4px alrededor.
  addCameraOwnershipLayer(map)
  addStaticLayers(map)
  // Velo de tinte del ámbito sobre el ícono de cámara — DESPUÉS de los
  // iconos estáticos para componer encima del fondo teal.
  addCameraOwnershipTintLayer(map)
  addDynamicLayers(map)
  // Badge del proveedor externo (Carbyne / DiDi / Uber / RapidSOS).
  // Va DESPUES de los iconos base para quedar visualmente encima de
  // ellos. Filtra por `['has', 'partner']` así solo aparece en
  // reportantes e incidents enriquecidos con el systemName/uber/etc.
  addPartnerBadgeLayer(map)
  // Etiquetas de texto en capas dedicadas. Se ponen DESPUES de los iconos
  // para que el texto quede visualmente arriba de los markers que estan
  // detras de ellos en el mismo bbox.
  addLabelLayers(map)
  // Overlay tenue del halo outer ENCIMA de los iconos. Razón: los halos
  // base van debajo de los iconos, así los iconos vecinos (cámaras/LPRs
  // etc.) tapan la porción del halo que se extiende sobre ellos. Este
  // overlay repinta el halo con opacidad muy baja por encima, dejando
  // visible el tinte del color del alert sobre los markers vecinos sin
  // saturarlos. No se aplica al halo inner (queda solo debajo del icono
  // del propio alert).
  addHaloOuterOverlayLayer(map)
}

// Safe addLayer — skip if already exists + try/catch para que una layer
// rota no interrumpa la cadena de addAllLayers. Loguea el error al stack
// de consola para diagnóstico sin esconder el bug.
function safeAddLayer(map, layerDef) {
  if (map.getLayer(layerDef.id)) return
  try {
    map.addLayer(layerDef)
  } catch (e) {
    console.error('[layer-manager] addLayer failed for', layerDef.id, e)
  }
}

function addJurisdictionBoundary(map) {
  // Sources persistentes (frontera estática, una vez por sesión).
  if (!map.getSource(JURISDICTION_SOURCE)) {
    map.addSource(JURISDICTION_SOURCE, {
      type: 'geojson',
      data: EDOMEX_BOUNDARY_GEOJSON,
    })
  }
  if (!map.getSource(JURISDICTION_MASK_SOURCE)) {
    map.addSource(JURISDICTION_MASK_SOURCE, {
      type: 'geojson',
      data: EDOMEX_MASK_GEOJSON,
    })
  }

  var accent = getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-accent').trim() || '#00D4FF'

  // 1. MASK externo: oscurece todo fuera del EdoMex. Opacity más agresiva
  //    que la versión anterior para crear contraste real sobre el dark
  //    background.
  safeAddLayer(map, {
    id: LAYERS.JURISDICTION_MASK,
    type: 'fill',
    source: JURISDICTION_MASK_SOURCE,
    paint: {
      'fill-color': '#000000',
      'fill-opacity': ['interpolate', ['linear'], ['zoom'], 5, 0.15, 9, 0.35, 13, 0.50],
    },
  })

  // 3. LINE GLOW: outline ancho y blurry que simula un halo alrededor del
  //    estado. Mayor width + menor opacity + no dash = efecto de resplandor.
  safeAddLayer(map, {
    id: LAYERS.JURISDICTION_LINE_GLOW,
    type: 'line',
    source: JURISDICTION_SOURCE,
    paint: {
      'line-color': accent,
      'line-width': ['interpolate', ['linear'], ['zoom'], 5, 5, 9, 8, 13, 12],
      'line-opacity': 0.2,
      'line-blur': 4,
    },
  })

  // 4. LINE SHARP: outline fino sólido que define el edge. Sobre el glow,
  //    se ve como una línea nítida dentro del halo. Non-dashed: borde
  //    autoritativo de "esta es mi jurisdicción" en lugar de "selección
  //    temporal".
  safeAddLayer(map, {
    id: LAYERS.JURISDICTION_LINE,
    type: 'line',
    source: JURISDICTION_SOURCE,
    paint: {
      'line-color': accent,
      'line-width': ['interpolate', ['linear'], ['zoom'], 5, 1.2, 9, 1.6, 13, 2],
      'line-opacity': 0.8,
    },
  })
}

function addGeofenceLayers(map) {
  // 3 capas por geocerca para que el perímetro sea legible incluso con
  // overlap profundo (sectores dentro de regiones, cuadrantes anidados):
  //   1. fill: tinte sutil del color del área (alpha bajo).
  //   2. line halo: línea negra ancha y blureada como drop-shadow.
  //   3. line: borde de color sólido encima del halo.
  // El protagonista visual es el borde + halo. El fill solo sugiere el
  // tinte; mantenerlo bajo evita que polígonos pequeños se confundan con
  // el fondo cuando una geocerca grande del mismo color los cubre.
  // Diferenciación regular vs mando único por grosor (3 vs 2) — misma
  // jerarquía que el legacy (Map.vue:790-834).
  var IS_MANDO_UNICO = ['match',
    ['get', 'mandounico'],
    ['region', 'sector', 'cuadrante'], true,
    false,
  ]

  // Fill alpha en niveles cercanos al legacy (Map.vue:805/832: regular
  // 0.35, mando único 0.20). Subimos un pelín por encima del legacy
  // porque el tema dark de MapV2 absorbe más alpha que el tema claro de
  // OpenLayers. La interpolación zoom-dependiente baja la opacidad a
  // baja escala — cuando el operador ve EdoMex completo y aparecen
  // muchas regiones, el fill no se acumula a un color barroso.
  safeAddLayer(map, {
    id: LAYERS.GEOFENCE_FILL,
    type: 'fill',
    source: GEOFENCE_SOURCE,
    paint: {
      'fill-color': ['get', 'fillColor'],
      // MapLibre exige que `interpolate(['zoom'])` sea TOP-LEVEL — no se
      // permite anidar dos interpolates dentro de un `case`. Por eso el
      // factor por categoría (mando único vs regular) se aplica DENTRO de
      // cada stop con `case`. Mismo patrón que buildIconSizeExpression
      // más abajo. Si lo escribes al revés MapLibre tira:
      //   "Only one zoom-based step/interpolate subexpression may be used".
      'fill-opacity': ['interpolate', ['linear'], ['zoom'],
        6,  ['case', IS_MANDO_UNICO, 0.05, 0.08],
        10, ['case', IS_MANDO_UNICO, 0.08, 0.12],
        14, ['case', IS_MANDO_UNICO, 0.10, 0.18],
      ],
    },
  })

  // Halo oscuro debajo del borde principal — funciona como "drop shadow"
  // que asegura legibilidad cuando borderColor es claro (blanco/amarillo)
  // sobre tiles ya oscuros del tema. Sin esto, una geocerca con
  // borderColor: '#ffffff' se ve casi del mismo tono que el fondo.
  safeAddLayer(map, {
    id: LAYERS.GEOFENCE_LINE + '-halo',
    type: 'line',
    source: GEOFENCE_SOURCE,
    paint: {
      'line-color': '#000000',
      'line-width': ['case',
        IS_MANDO_UNICO, 4,
        5,
      ],
      'line-opacity': 0.45,
      'line-blur': 1,
    },
  })

  safeAddLayer(map, {
    id: LAYERS.GEOFENCE_LINE,
    type: 'line',
    source: GEOFENCE_SOURCE,
    paint: {
      'line-color': ['get', 'borderColor'],
      'line-width': ['case',
        IS_MANDO_UNICO, 2,
        3,
      ],
      'line-opacity': 1,
    },
  })
}

function addClusterLayers(map) {
  // Hidden circle layer: kept as a click-target for cluster expand.
  // It is invisible because the breakdown card (CLUSTER_IMG_SOURCE) is the
  // visible representation.
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_CLUSTERS,
    type: 'circle',
    source: UNIFIED_SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-radius': 24,
      'circle-color': 'rgba(0, 0, 0, 0)',
      'circle-stroke-color': 'rgba(0, 0, 0, 0)',
      'circle-stroke-width': 0,
    },
  })

  // Rich cluster breakdown cards rendered from the secondary source.
  // Each feature carries an `imageId` property that references a
  // Canvas-generated image registered with map.addImage().
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_CLUSTER_ICONS,
    type: 'symbol',
    source: CLUSTER_IMG_SOURCE,
    layout: {
      'icon-image': ['get', 'imageId'],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  })
}

// Factores de tamaño y opacidad por estado.
// Baseline uniforme: todos los markers se dibujan al 100% del zoom-base
// independientemente de si están alertados o no. La jerarquía visual de
// "atención" se comunica por el halo y el pulse (capas separadas), NO por
// tamaño del icono. Esto evita que los markers dormant queden al 70% y
// se vean ilegibles (feedback del operador).
//
// Única excepción: `high` (incidente prioridad ALTA) +15% para reforzar
// el caso más crítico sin chico-grande-gigantic de tres escalones.
//
// Selected: +5% muy sutil para que el seleccionado tenga un realce mínimo
// sin perder la referencia con los vecinos. El halo de selección ya hace
// el trabajo visual principal.
var ICON_SIZE_FACTOR_EXPR = ['case',
  ['==', ['get', 'selected'], true], 1.05,
  ['match', ['get', 'alertLevel'], ['high'], 1.15, 1.0],
]
// Opacidad uniforme: el glow del halo se encarga de destacar alertas.
// Los dormant ya no se atenúan al 75% — antes el operador los confundía
// con markers "apagados" o veía el mapa como "descolorido".
var ICON_OPACITY_FACTOR_EXPR = 1.0
var ICON_STATUS_DOWN_FACTOR = ['case',
  ['==', ['get', 'iconStatus'], 'down'], 0.5,
  1,
]

function buildIconSizeExpression() {
  // MapLibre regla: interpolate(['zoom']) debe ser top-level. NO anidable
  // en ['*', ...]. Por eso el factor por estado (dormant/alert/selected) se
  // aplica DENTRO de cada stop multiplicando ['*', base, factor].
  //
  // Cap en 1.0 desde zoom 14: el bitmap tiene 40×40 px. Cada unidad de
  // icon-size sobre 1.0 fuerza a WebGL a upsamplear la textura con LINEAR
  // filter → bordes borrosos. Antes teníamos stops hasta 1.25@z16 para que
  // los markers se percibieran "más grandes" al acercar, pero el costo
  // visual (bordes suaves) superó al beneficio. Zoom ≥14 ahora queda fijo
  // en 1.0 — el usuario percibe acercamiento via el mapa mismo (calles,
  // detalles geográficos), no por crecer el marker.
  return [
    'interpolate', ['linear'], ['zoom'],
    5,  ['*', 0.55, ICON_SIZE_FACTOR_EXPR],
    12, ['*', 0.80, ICON_SIZE_FACTOR_EXPR],
    14, ['*', 1.00, ICON_SIZE_FACTOR_EXPR],
  ]
}

function buildIconOpacityExpression() {
  return ['*', ICON_STATUS_DOWN_FACTOR, ICON_OPACITY_FACTOR_EXPR]
}

// Radios base de los halos (en pixels) × factor por alertLevel. MapLibre
// exige que interpolate(zoom) sea TOP-LEVEL — ['*', interpolate, factor]
// NO es válido y rompe addLayer. Por eso multiplicamos DENTRO de cada
// stop del interpolate.
// Para el radio del halo, 'base', 'high' y 'medium' usan factor 1.15
// (marker 115%); 'low' y 'resolved' se renderizan con factor 0.85 (glow
// discreto pero presente, más chico que MEDIA para que la jerarquía
// visual siga comunicando urgencia); 'none' queda en default 0.7 pero
// nunca llega ya que el filter lo excluye.
var HALO_ALERT_SIZE_FACTOR = ['match', ['get', 'alertLevel'],
  ['base', 'high', 'medium'], 1.15,
  ['low', 'resolved'], 0.85,
  0.7,
]

function buildHaloOuterRadiusExpr() {
  return [
    'interpolate', ['linear'], ['zoom'],
    5,  ['*', 18, HALO_ALERT_SIZE_FACTOR],
    10, ['*', 22, HALO_ALERT_SIZE_FACTOR],
    13, ['*', 28, HALO_ALERT_SIZE_FACTOR],
    15, ['*', 36, HALO_ALERT_SIZE_FACTOR],
    17, ['*', 46, HALO_ALERT_SIZE_FACTOR],
  ]
}

function buildHaloInnerRadiusExpr() {
  return [
    'interpolate', ['linear'], ['zoom'],
    5,  ['*', 13, HALO_ALERT_SIZE_FACTOR],
    10, ['*', 17, HALO_ALERT_SIZE_FACTOR],
    13, ['*', 21, HALO_ALERT_SIZE_FACTOR],
    15, ['*', 27, HALO_ALERT_SIZE_FACTOR],
    17, ['*', 34, HALO_ALERT_SIZE_FACTOR],
  ]
}

// Filter del halo INTERNO: se muestra en alertLevel ∈ {base, high, medium,
// low, resolved}. Incluye incident MEDIA (solo halo interno, spec §2.2),
// incident BAJA (glow discreto por feedback del stakeholder) y sensor
// reportado (alerta atendida pendiente de cierre — halo ámbar sutil).
var HALO_INNER_FILTER = ['all',
  ['!', ['has', 'point_count']],
  ['match', ['get', 'alertLevel'], ['base', 'high', 'medium', 'low', 'resolved'], true, false],
]

// Filter del halo EXTERNO: solo base y high. 'medium' (incident MEDIA)
// se queda solo con halo interno (spec §2.2). 'low' (BAJA) sin halos.
var HALO_OUTER_BASE_FILTER = ['all',
  ['!', ['has', 'point_count']],
  ['match', ['get', 'alertLevel'], ['base', 'high'], true, false],
]
// Carril "pulse": features con pulseActive=true. rAF de MapV2 modula la
// opacidad de esta capa via setPaintProperty (una call por frame por layer,
// no por feature).
var HALO_OUTER_PULSE_FILTER = ['all',
  HALO_OUTER_BASE_FILTER,
  ['==', ['get', 'pulseActive'], true],
]
var HALO_OUTER_STATIC_FILTER = ['all',
  HALO_OUTER_BASE_FILTER,
  ['!=', ['get', 'pulseActive'], true],
]

function addHaloLayers(map) {
  // Halo externo (estático) — MEDIA / bus alert / sensor >90s / etc.
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_HALO_OUTER_STATIC,
    type: 'circle',
    source: UNIFIED_SOURCE,
    filter: HALO_OUTER_STATIC_FILTER,
    paint: {
      'circle-color': ['get', 'haloColor'],
      'circle-radius': buildHaloOuterRadiusExpr(),
      'circle-opacity': 0.2,  // estático; §1.3 spec
      'circle-stroke-width': 0,
    },
  })

  // Halo externo (pulsante) — mismo paint base; la opacidad la modula
  // MapV2 en rAF via setPaintProperty cuando hay al menos 1 feature con
  // pulseActive=true. Entre 0.15 y 0.35 (§3.2 spec).
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_HALO_OUTER_PULSE,
    type: 'circle',
    source: UNIFIED_SOURCE,
    filter: HALO_OUTER_PULSE_FILTER,
    paint: {
      'circle-color': ['get', 'haloColor'],
      'circle-radius': buildHaloOuterRadiusExpr(),
      'circle-opacity': 0.25,  // valor medio inicial; modulable en runtime
      'circle-stroke-width': 0,
    },
  })

  // Halo interno (siempre estático, sin pulse) — opacidad 40%.
  // Se muestra también en incident MEDIA (alertLevel='medium').
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_HALO_INNER,
    type: 'circle',
    source: UNIFIED_SOURCE,
    filter: HALO_INNER_FILTER,
    paint: {
      'circle-color': ['get', 'haloColor'],
      'circle-radius': buildHaloInnerRadiusExpr(),
      'circle-opacity': 0.4,
      'circle-stroke-width': 0,
    },
  })
}

// Overlay tenue del halo outer, renderizado ENCIMA de los iconos (se
// agrega a addAllLayers después de addDynamicLayers). Solo para alerts
// base y high — MEDIA solo tiene halo interno y no necesita overlay.
// Opacidad 0.10 (muy tenue) — suficiente para teñir markers vecinos del
// color del alert sin enmascararlos.
function addHaloOuterOverlayLayer(map) {
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_HALO_OUTER_OVERLAY,
    type: 'circle',
    source: UNIFIED_SOURCE,
    filter: HALO_OUTER_BASE_FILTER,
    paint: {
      'circle-color': ['get', 'haloColor'],
      'circle-radius': buildHaloOuterRadiusExpr(),
      'circle-opacity': 0.10,
      'circle-stroke-width': 0,
      'circle-blur': 0.2,
    },
  })
}

// Punto pequeño que indica el centro real cuando un grupo de incidentes
// se renderiza con spread radial. Sin esto, el operador ve N markers
// flotando sin saber dónde está el lat/lon original. El círculo es
// blanco semi-transparente, 4 px de radio — bastante chico para no
// competir con los iconos pero claro como "anchor" visual.
//
// La layer no usa una source dedicada — comparte UNIFIED_SOURCE filtrando
// por spreadActive=true. Como hay N features con la misma coord, el círculo
// se dibuja N veces (overdraw). Con circle-opacity 0.5 y blend, el resultado
// se ve un poco más sólido en grupos densos, lo cual sirve de indicador
// visual de "aquí hay varios" reforzando la idea del spread.
function addSpreadCenterLayer(map) {
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_SPREAD_CENTER,
    type: 'circle',
    source: UNIFIED_SOURCE,
    filter: ['all',
      ['!', ['has', 'point_count']],
      ['==', ['get', 'spreadActive'], true],
    ],
    paint: {
      'circle-radius': 2,
      'circle-color': 'rgba(255, 255, 255, 0.85)',
      'circle-stroke-color': 'rgba(0, 0, 0, 0.45)',
      'circle-stroke-width': 0.75,
      'circle-opacity': 0.85,
    },
  })
}

// Imagen SDF de un rounded-rect relleno, generada analíticamente (la
// distancia firmada a un rounded-rect tiene fórmula cerrada, no hace falta
// rasterizar+escanear). Convención tiny-sdf/MapLibre: cutoff 0.25 → el
// shader dibuja el edge donde alpha ≈ 0.75·255, que aquí cae exactamente
// en distancia 0. SDF permite recolorear via `icon-color` data-driven —
// un solo asset para los 3 ámbitos y cualquier tema.
function makeRoundedRectSDF(size, rectSize, cornerRadius) {
  var data = new Uint8Array(size * size * 4)
  var half = size / 2
  var innerHalf = rectSize / 2 - cornerRadius
  var cutoff = 0.25
  var spread = size / 6  // px de rango del gradiente SDF
  for (var y = 0; y < size; y++) {
    for (var x = 0; x < size; x++) {
      var dx = Math.abs(x + 0.5 - half) - innerHalf
      var dy = Math.abs(y + 0.5 - half) - innerHalf
      var ox = Math.max(dx, 0)
      var oy = Math.max(dy, 0)
      // Distancia firmada al rounded-rect: >0 fuera, <0 dentro
      var sd = Math.sqrt(ox * ox + oy * oy) + Math.min(Math.max(dx, dy), 0) - cornerRadius
      var alpha = Math.round(255 * Math.min(Math.max(1 - (sd / spread + cutoff), 0), 1))
      var i = (y * size + x) * 4
      data[i] = 255
      data[i + 1] = 255
      data[i + 2] = 255
      data[i + 3] = alpha
    }
  }
  return { width: size, height: size, data: data }
}

function addCameraOwnershipLayer(map) {
  // pixelRatio 2: tamaño lógico 50px (marker de 40px + ~4-5px de borde
  // visible por lado a icon-size 1.0) con nitidez en pantallas HiDPI.
  if (!map.hasImage(LAYERS.CAMERA_OWNERSHIP_RING)) {
    try {
      map.addImage(LAYERS.CAMERA_OWNERSHIP_RING, makeRoundedRectSDF(100, 96, 28), { sdf: true, pixelRatio: 2 })
    } catch (e) {
      console.error('[layer-manager] addImage ownership ring failed', e)
    }
  }

  safeAddLayer(map, {
    id: LAYERS.CAMERA_OWNERSHIP_RING,
    type: 'symbol',
    source: UNIFIED_SOURCE,
    filter: ['all',
      ['!', ['has', 'point_count']],
      // Cámaras (ámbito/color del grupo) y botones de pánico (color del
      // grupo) comparten canvas de 40px con fondo de 34px → misma placa.
      ['in', ['get', 'entityType'], ['literal', ['camera', 'sensor']]],
      // '' = grupo sin ambito NI color configurado (o registry aún no
      // cargado) → sin placa. Sin heurísticas: solo config explícita.
      ['!=', ['get', 'ownershipColor'], ''],
    ],
    layout: {
      'icon-image': LAYERS.CAMERA_OWNERSHIP_RING,
      // Mismos stops que UNIFIED_STATIC_ICONS para que la placa escale en
      // sincronía exacta con el marker que abraza.
      'icon-size': buildIconSizeExpression(),
      'icon-allow-overlap': true,
      'symbol-sort-key': ['get', 'iconSortKey'],
      // Spread radial — mismo offset que el ícono (ver UNIFIED_STATIC_ICONS);
      // icon-offset se multiplica por icon-size en ambas layers por igual.
      'icon-offset': ['coalesce', ['get', 'iconOffsetPx'], ['literal', [0, 0]]],
    },
    paint: {
      // Resuelto por feature en monitor-normalizer (override del grupo o
      // token de paleta del ámbito) — permite color custom por grupo sin
      // re-crear la layer.
      'icon-color': ['get', 'ownershipColor'],
      // 0.72 de base: la placa es marco informativo, no protagonista —
      // deja pasar el basemap y suaviza el bloque de color. Se multiplica
      // por el factor por estado (offline hereda la atenuación del ícono).
      'icon-opacity': ['*', 0.72, buildIconOpacityExpression()],
      'icon-opacity-transition': { duration: 0, delay: 0 },
    },
  })
}

// Velo del color del ámbito sobre el fondo del ícono de cámara. Segunda
// imagen SDF al tamaño exacto del rect del fondo del marker (34px lógicos,
// inset 3 del canvas de 40) — NO se reusa la placa escalada por icon-size
// porque icon-offset se multiplica por icon-size y el spread radial
// quedaría desalineado respecto al ícono.
function addCameraOwnershipTintLayer(map) {
  if (!map.hasImage(LAYERS.CAMERA_OWNERSHIP_TINT)) {
    try {
      map.addImage(LAYERS.CAMERA_OWNERSHIP_TINT, makeRoundedRectSDF(76, 68, 18), { sdf: true, pixelRatio: 2 })
    } catch (e) {
      console.error('[layer-manager] addImage ownership tint failed', e)
    }
  }

  safeAddLayer(map, {
    id: LAYERS.CAMERA_OWNERSHIP_TINT,
    type: 'symbol',
    source: UNIFIED_SOURCE,
    filter: ['all',
      ['!', ['has', 'point_count']],
      ['in', ['get', 'entityType'], ['literal', ['camera', 'sensor']]],
      ['!=', ['get', 'ownershipColor'], ''],
      // Botón de pánico en alerta: sin velo, para no alterar el rojo de la
      // alerta. La placa (borde) sí se mantiene para conservar el grupo.
      ['!', ['all',
        ['==', ['get', 'entityType'], 'sensor'],
        ['==', ['get', 'status'], 'alert'],
      ]],
    ],
    layout: {
      'icon-image': LAYERS.CAMERA_OWNERSHIP_TINT,
      'icon-size': buildIconSizeExpression(),
      'icon-allow-overlap': true,
      'symbol-sort-key': ['get', 'iconSortKey'],
      'icon-offset': ['coalesce', ['get', 'iconOffsetPx'], ['literal', [0, 0]]],
    },
    paint: {
      'icon-color': ['get', 'ownershipColor'],
      // Velo sutil: tiñe el teal hacia el color del dueño sin taparlo ni
      // ensuciar el glifo. Hereda la atenuación por estado del ícono.
      'icon-opacity': ['*', 0.20, buildIconOpacityExpression()],
      'icon-opacity-transition': { duration: 0, delay: 0 },
    },
  })
}

function addStaticLayers(map) {
  var staticFilter = ['all',
    ['!', ['has', 'point_count']],
    ['any', ['==', ['get', 'isStatic'], true], ['==', ['get', 'isStatic'], 'true']],
  ]

  // Icons only — no circle backgrounds. Texto delegado a UNIFIED_LABELS_*.
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_STATIC_ICONS,
    type: 'symbol',
    source: UNIFIED_SOURCE,
    filter: staticFilter,
    layout: {
      'icon-image': buildIconExpression(),
      'icon-size': buildIconSizeExpression(),
      'icon-allow-overlap': true,
      // Con icon-allow-overlap=true, MapLibre dibuja MAYOR sort-key encima.
      // iconSortKey en monitor-normalizer es positivo (labelPriority * 1M)
      // para que incident/sos_call/sensor:alert queden visualmente arriba
      // de los markers vecinos. Sin esta linea, un sensor en alerta podia
      // quedar tapado por camaras online vecinas con mismo zoom-base.
      'symbol-sort-key': ['get', 'iconSortKey'],
      // Spread radial permanente: cuando un grupo de incidentes comparte
      // coord exacta, overlap-spreader.js asigna un offset en píxeles
      // distinto a cada uno para que el operador vea TODOS visibles.
      // Default [0, 0] (seteado por monitor-normalizer) = sin spread.
      'icon-offset': ['coalesce', ['get', 'iconOffsetPx'], ['literal', [0, 0]]],
    },
    paint: {
      'icon-opacity': buildIconOpacityExpression(),
      // Sin transición de fade. Cada fade que dispara MapLibre crea timers
      // internos que se acumulan en sesiones largas con tráfico WS denso
      // (visto: 32 MB de DOMTimers + cientos de MB en closures Function/Context
      // retenidos por el imageManager.painter). Sacrificamos el fade-in/out
      // suave (icono entra/sale instantáneo al cambiar zoom o toggle de capa)
      // a cambio de no acumular memoria sin tope.
      'icon-opacity-transition': { duration: 0, delay: 0 },
    },
  })
}

function addDynamicLayers(map) {
  var dynamicFilter = ['all',
    ['!', ['has', 'point_count']],
    ['any', ['==', ['get', 'isStatic'], false], ['==', ['get', 'isStatic'], 'false']],
  ]

  // Solo iconos. Las etiquetas de texto se manejan en capas dedicadas
  // (UNIFIED_LABELS_COLLIDE / UNIFIED_LABELS_PRIORITY) para poder
  // configurarlas independientemente del draw-order del icono.
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_DYNAMIC_ICONS,
    type: 'symbol',
    source: UNIFIED_SOURCE,
    filter: dynamicFilter,
    layout: {
      'icon-image': buildIconExpression(),
      'icon-size': buildIconSizeExpression(),
      'icon-allow-overlap': true,
      // iconSortKey positivo: priority alta queda visualmente encima.
      // Ver comentario equivalente en UNIFIED_STATIC_ICONS.
      'symbol-sort-key': ['get', 'iconSortKey'],
      // Spread radial — ver comentario en UNIFIED_STATIC_ICONS.
      'icon-offset': ['coalesce', ['get', 'iconOffsetPx'], ['literal', [0, 0]]],
    },
    paint: {
      'icon-opacity': buildIconOpacityExpression(),
      // Sin fade transition — ver UNIFIED_STATIC_ICONS para racional.
      'icon-opacity-transition': { duration: 0, delay: 0 },
    },
  })
}

// Badge superpuesto con el logo del proveedor externo de interconexión
// (Carbyne / DiDi / Uber / RapidSOS). El feature.properties.partner trae
// el id del proveedor; el sprite se llama `partner-badge-<id>` y se
// pre-registra en `icon-registry.registerPartnerBadges`. Filtramos por
// `['has', 'partner']` para que solo afecte a reportantes e incidents
// enriquecidos con la info del sistema externo.
//
// `icon-offset` posiciona el badge sobre la esquina superior-derecha
// del marker base (40×40 → desplazamiento de +14 y −14 en pixeles del
// SVG original; MapLibre lo escala con icon-size:0.6).
function addPartnerBadgeLayer(map) {
  var filter = ['all',
    ['!', ['has', 'point_count']],
    ['has', 'partner'],
  ]
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_PARTNER_BADGE,
    type: 'symbol',
    source: UNIFIED_SOURCE,
    filter: filter,
    layout: {
      'icon-image': ['concat', 'partner-badge-', ['get', 'partner']],
      // Sprite es 48×48 (ver registerPartnerBadges). icon-size 0.30 →
      // ~14px efectivos en pantalla: lectura clara del logo SIN tapar
      // el icono central del marker base 40×40 (antes 0.55 → 26px
      // cubría 66% del marker, el badge parecía el icono principal).
      // icon-offset se escala con icon-size; con size 0.30 los valores
      // [55, -50] colocan el centro del badge a +16.5/-15 px del centro
      // del marker (esquina arriba-derecha sobresaliendo ~4px del
      // borde del marker).
      'icon-size': 0.30,
      'icon-offset': [55, -50],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      // Mismo sort key que el icono base — el badge sigue al marker en
      // capas superiores cuando hay solapamiento de iconos.
      'symbol-sort-key': ['get', 'iconSortKey'],
    },
    paint: {
      'icon-opacity': 1,
    },
  })
}

// ── Label layers ────────────────────────────────────────────────────
// Las etiquetas de texto viven en dos capas separadas con fines distintos:
//
// 1. UNIFIED_LABELS_PRIORITY (filter: labelPriority>=3) — incident, sos_call,
//    reportant, sensor:alert. SIEMPRE visible, text-allow-overlap=true,
//    text-ignore-placement=true. El operador necesita ver SIEMPRE estas
//    etiquetas independientemente del modo o de cuantos iconos haya cerca.
//
// 2. UNIFIED_LABELS_COLLIDE (filter: labelPriority<3) — todo el resto.
//    Su comportamiento varia segun el labelMode (dense/smart/minimal):
//      dense   = text-allow-overlap=true,  text-ignore-placement=true
//                (mapa lleno; todas las etiquetas visibles aunque se
//                superpongan)
//      smart   = text-allow-overlap=false, text-padding=1
//                (collision detection con padding pequeno; mas etiquetas
//                que el comportamiento default de MapLibre)
//      minimal = text-allow-overlap=false, text-padding=2 (default)
//                + filter excluye estaticos
//                (solo entidades dinamicas obtienen label, como el
//                comportamiento legacy)
//
// Filters/layout properties los muta applyLabelMode() en runtime via
// setFilter/setLayoutProperty.
// Default del minZoom — lo usa addLabelLayers en el boot. applyLabelMode
// lo sobreescribe en runtime con el valor efectivo (clusterMaxZoom+1
// cuando el clustering esta activo, o 0 si esta apagado).
var DEFAULT_LABEL_MIN_ZOOM = 14

function buildLabelTextField(minZoom) {
  var z = (minZoom == null) ? DEFAULT_LABEL_MIN_ZOOM : minZoom
  return ['step', ['zoom'], '', z, ['coalesce', ['get', 'label'], ['get', 'title']]]
}

function buildLabelLayout(extra, minZoom) {
  var base = {
    'text-field': buildLabelTextField(minZoom),
    'text-font': ['Open Sans Bold'],
    'text-size': 11,
    // Cuando el feature está spreadeado (incidente en grupo de coords
    // duplicadas), overlap-spreader.js precomputa textOffsetEm con el
    // offset del icono convertido a em (text-size=11 → 1em=11px) más el
    // baseline 1.8em. Así la label queda debajo de SU marker y no
    // apilada con las demás del grupo en el centro real.
    'text-offset': ['coalesce', ['get', 'textOffsetEm'], ['literal', [0, 1.8]]],
    'text-anchor': 'top',
    'text-max-width': 12,
    'text-optional': true,
    // symbol-sort-key NEGATIVO en estas capas. MapLibre con
    // text-allow-overlap=false prioriza placement de MENOR sort-key →
    // priority alta gana las colisiones. Para PRIORITY (allow-overlap=true)
    // el sort-key es irrelevante para placement pero sigue ordenando el
    // draw-order entre etiquetas que se superponen.
    'symbol-sort-key': ['get', 'labelSortKey'],
  }
  if (extra) {
    var keys = Object.keys(extra)
    for (var i = 0; i < keys.length; i++) base[keys[i]] = extra[keys[i]]
  }
  return base
}

function buildLabelPaint() {
  var rootStyle = getComputedStyle(document.documentElement)
  var halo = rootStyle.getPropertyValue('--kln-bg-overlay').trim() || 'rgba(10, 14, 26, 0.85)'
  // text-color del tema (no hardcodear blanco): en temas oscuros es off-white
  // y en el tema claro 'diurno' es navy oscuro, legible sobre mapa claro.
  // El halo (--kln-bg-overlay) ya es del color del fondo del tema, así que
  // texto+halo siempre contrastan con el mapa. (CLAUDE.md patrón #20.)
  var textColor = rootStyle.getPropertyValue('--kln-text').trim() || '#ffffff'
  return {
    'text-color': textColor,
    'text-halo-color': halo,
    'text-halo-width': 1.5,
    'text-opacity': ['interpolate', ['linear'], ['zoom'], 14, 0.7, 16, 1],
    // Sin fade transition — ver UNIFIED_STATIC_ICONS para racional
    // (acumulación de DOMTimers + closures retenidos en sesiones largas).
    'text-opacity-transition': { duration: 0, delay: 0 },
  }
}

// Filtros base. labelPriority es siempre numerico 1..4 emitido por el
// monitor-normalizer. Los clusters quedan excluidos por el filtro
// previo `['!', ['has', 'point_count']]` antes de evaluar la propiedad,
// asi que NO necesitamos `coalesce`/`to-number` aqui (eran overhead por
// frame por feature en el render path de MapLibre).
var LABEL_COLLIDE_FILTER_DEFAULT = ['all',
  ['!', ['has', 'point_count']],
  ['<', ['get', 'labelPriority'], 3],
]
// Para minimal mode: ademas excluye estaticos para mantener el
// comportamiento legacy donde camaras/sensores no muestran label.
var LABEL_COLLIDE_FILTER_MINIMAL = ['all',
  ['!', ['has', 'point_count']],
  ['<', ['get', 'labelPriority'], 3],
  ['any', ['==', ['get', 'isStatic'], false], ['==', ['get', 'isStatic'], 'false']],
]
var LABEL_PRIORITY_FILTER = ['all',
  ['!', ['has', 'point_count']],
  ['>=', ['get', 'labelPriority'], 3],
]

function addLabelLayers(map) {
  // Layer 1: criticas (priority >= 3) — siempre encima sin colision detection.
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_LABELS_PRIORITY,
    type: 'symbol',
    source: UNIFIED_SOURCE,
    filter: LABEL_PRIORITY_FILTER,
    layout: buildLabelLayout({
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    }),
    paint: buildLabelPaint(),
  })

  // Layer 2: el resto. Default = smart mode (allow-overlap:false, padding:1).
  // applyLabelMode() las muta segun el toggle del operador.
  safeAddLayer(map, {
    id: LAYERS.UNIFIED_LABELS_COLLIDE,
    type: 'symbol',
    source: UNIFIED_SOURCE,
    filter: LABEL_COLLIDE_FILTER_DEFAULT,
    layout: buildLabelLayout({
      'text-allow-overlap': false,
      'text-ignore-placement': false,
      'text-padding': 1,
    }),
    paint: buildLabelPaint(),
  })
}

/**
 * Aplica el modo de visualizacion de etiquetas a las layers de texto.
 * Modos: 'dense' | 'smart' | 'minimal'. La layer PRIORITY no se toca
 * (siempre visible).
 *
 * dense   → todas las etiquetas siempre visibles (sin colision detection).
 * smart   → collision detection con padding pequeno; mas etiquetas que el
 *           default de MapLibre, priorizadas por labelSortKey.
 * minimal → comportamiento legacy: solo dinamicas, collision detection
 *           con padding default.
 *
 * @param {Object} map - MapLibre instance.
 * @param {string} mode - dense | smart | minimal.
 * @param {number} [minZoom] - zoom minimo a partir del cual se dibujan
 *   etiquetas. Lo usa MapV2 para enlazar el threshold con el termino del
 *   clustering (clusterMaxZoom+1) o 0 si el cluster esta apagado. Si se
 *   omite, conserva el text-field actual (no lo modifica).
 */
export function applyLabelMode(map, mode, minZoom) {
  if (!map) return
  if (!map.getLayer(LAYERS.UNIFIED_LABELS_COLLIDE)) return
  var safe = (mode === 'dense' || mode === 'minimal') ? mode : 'smart'

  if (minZoom != null) {
    var textField = buildLabelTextField(minZoom)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-field', textField)
    if (map.getLayer(LAYERS.UNIFIED_LABELS_PRIORITY)) {
      map.setLayoutProperty(LAYERS.UNIFIED_LABELS_PRIORITY, 'text-field', textField)
    }
  }

  if (safe === 'dense') {
    map.setFilter(LAYERS.UNIFIED_LABELS_COLLIDE, LABEL_COLLIDE_FILTER_DEFAULT)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-allow-overlap', true)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-ignore-placement', true)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-padding', 0)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'visibility', 'visible')
  } else if (safe === 'minimal') {
    map.setFilter(LAYERS.UNIFIED_LABELS_COLLIDE, LABEL_COLLIDE_FILTER_MINIMAL)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-allow-overlap', false)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-ignore-placement', false)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-padding', 2)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'visibility', 'visible')
  } else {
    // smart (default)
    map.setFilter(LAYERS.UNIFIED_LABELS_COLLIDE, LABEL_COLLIDE_FILTER_DEFAULT)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-allow-overlap', false)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-ignore-placement', false)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'text-padding', 1)
    map.setLayoutProperty(LAYERS.UNIFIED_LABELS_COLLIDE, 'visibility', 'visible')
  }
}

function addHeatmapLayer(map) {
  // Source dedicada SIN clustering para que el heatmap funcione a cualquier
  // zoom. La UNIFIED_SOURCE tiene cluster:true y a zoom bajo los puntos
  // individuales se agrupan en cluster features (que no tienen entityType
  // ni createdAtMs), dejando al heatmap sin nada que renderizar.
  // MapV2 popula esta source con syncHeatmapIncidents() desde _monitors.
  if (!map.getSource(HEATMAP_INCIDENTS_SOURCE)) {
    map.addSource(HEATMAP_INCIDENTS_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }

  safeAddLayer(map, {
    id: LAYERS.UNIFIED_HEATMAP,
    type: 'heatmap',
    source: HEATMAP_INCIDENTS_SOURCE,
    layout: { visibility: 'none' },
    paint: {
      // Peso aumenta a menor zoom para que la densidad sea legible desde
      // lejos (visión de patrones a nivel de municipio/estado).
      'heatmap-weight': 1,
      'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 5, 1.2, 12, 1.0, 16, 0.8],
      // Radio amplio a zoom bajo para ver "manchones" de densidad, decrece
      // en zoom alto donde ya se alcanzan a ver puntos individuales.
      'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 5, 22, 10, 30, 14, 40, 18, 22],
      'heatmap-color': [
        'interpolate', ['linear'], ['heatmap-density'],
        0,   'rgba(0, 0, 255, 0)',
        0.2, 'rgba(0, 120, 255, 0.55)',
        0.4, 'rgba(0, 220, 180, 0.70)',
        0.6, 'rgba(255, 240, 0, 0.80)',
        0.8, 'rgba(255, 140, 0, 0.90)',
        1.0, 'rgba(255, 40, 30, 0.95)',
      ],
      // Fade-out progresivo a zooms muy altos (>18) donde el ruido visual
      // del heatmap deja de ser útil y los markers individuales bastan.
      'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 5, 0.85, 15, 0.75, 18, 0.35, 20, 0.1],
    },
  })
}

function addTrailLayers(map) {
  if (!map.getSource(TRAIL_SOURCE)) {
    map.addSource(TRAIL_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })
  }

  var trailLineColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-trail-line').trim() || '#00BCD4'
  var alertSosColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-alert-sos').trim() || '#E84A3D'
  var alertWarningColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-alert-warning').trim() || '#D6A73A'

  // SOS trail — MARKERS-DESIGN-SPEC.md §6.1: línea firme roja-coral.
  safeAddLayer(map, {
    id: LAYERS.TRAIL_LINE_SOS,
    type: 'line',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'LineString'],
      ['==', ['get', 'entityType'], 'sos_call'],
    ],
    paint: {
      'line-color': ['coalesce', ['get', 'trailColor'], alertSosColor],
      'line-width': 3,
      'line-opacity': 0.85,
    },
  })

  // Flechas de direccion sobre el rastro de una llamada SOS. Mismo patron que
  // TRAIL_ARROWS_MC, que es el unico que las tenia.
  //
  // 'text-keep-upright': false es lo que hace que funcionen: por omision
  // MapLibre voltea los glifos de un symbol sobre linea para que nunca se lean
  // de cabeza, y con una flecha eso significa invertirla en cada tramo que va
  // hacia el poniente. Un rastro que miente sobre su direccion es peor que uno
  // que no la muestra.
  //
  // A diferencia del rastro historico del reportante, aqui NO se emiten
  // features por segmento: este rastro se redibuja con cada posicion GPS y con
  // el tope de 500 puntos serian hasta 500 features extra por update. Las
  // flechas van sobre la linea completa con spacing fijo, que es barato y se
  // ve practicamente igual.
  safeAddLayer(map, {
    id: LAYERS.TRAIL_ARROWS_SOS,
    type: 'symbol',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'LineString'],
      ['==', ['get', 'entityType'], 'sos_call'],
    ],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 60,
      'text-field': '\u25B6',
      'text-size': 11,
      'text-keep-upright': false,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': ['coalesce', ['get', 'trailColor'], alertSosColor],
      'text-halo-color': '#000',
      'text-halo-width': 1,
      'text-opacity': 0.9,
    },
  })
  safeAddLayer(map, {
    id: LAYERS.TRAIL_DOTS_SOS,
    type: 'circle',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'Point'],
      ['==', ['get', 'entityType'], 'sos_call'],
    ],
    paint: {
      'circle-radius': 4,
      'circle-color': ['coalesce', ['get', 'trailColor'], alertSosColor],
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 1,
    },
  })

  // Medida cautelar — el rastro tiene que responder tres preguntas de un
  // vistazo: dónde está ahora, de dónde viene y hacia dónde iba. Por eso los
  // puntos crecen y se opacan con `edad` (0 = más viejo, 1 = actual), el
  // último lleva un halo, y la línea va sembrada de flechas que marcan el
  // sentido de la marcha. Un rastro sin dirección no dice nada.
  var mcColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--kln-accent').trim() || '#6fb8ff'

  safeAddLayer(map, {
    id: LAYERS.TRAIL_LINE_MC,
    type: 'line',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'LineString'],
      ['==', ['get', 'entityType'], 'medida_cautelar'],
    ],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': mcColor,
      'line-width': 3,
      'line-opacity': 0.7,
    },
  })
  safeAddLayer(map, {
    id: LAYERS.TRAIL_ARROWS_MC,
    type: 'symbol',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'LineString'],
      ['==', ['get', 'entityType'], 'medida_cautelar'],
    ],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 60,
      'text-field': '\u25B6',
      'text-size': 11,
      'text-keep-upright': false,
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': mcColor,
      'text-halo-color': '#000',
      'text-halo-width': 1,
    },
  })
  safeAddLayer(map, {
    id: LAYERS.TRAIL_DOTS_MC,
    type: 'circle',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'Point'],
      ['==', ['get', 'entityType'], 'medida_cautelar'],
    ],
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['get', 'edad'], 0, 2.5, 1, 7],
      'circle-color': mcColor,
      'circle-opacity': ['interpolate', ['linear'], ['get', 'edad'], 0, 0.3, 1, 1],
      'circle-stroke-color': '#fff',
      'circle-stroke-width': ['case', ['==', ['get', 'actual'], true], 2.5, 0],
    },
  })

  // Reportant trail — §6.2: rastro discreto amber dashed 2px, 55%.
  safeAddLayer(map, {
    id: LAYERS.TRAIL_LINE_REPORTANT,
    type: 'line',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'LineString'],
      ['==', ['get', 'entityType'], 'reportant'],
    ],
    paint: {
      'line-color': ['coalesce', ['get', 'trailColor'], alertWarningColor],
      'line-width': 2,
      'line-dasharray': [3, 2],
      'line-opacity': 0.55,
    },
  })

  // Flechas de direccion sobre el rastro de un reportante seguido en vivo. Mismo patron que
  // TRAIL_ARROWS_MC, que es el unico que las tenia.
  //
  // 'text-keep-upright': false es lo que hace que funcionen: por omision
  // MapLibre voltea los glifos de un symbol sobre linea para que nunca se lean
  // de cabeza, y con una flecha eso significa invertirla en cada tramo que va
  // hacia el poniente. Un rastro que miente sobre su direccion es peor que uno
  // que no la muestra.
  //
  // A diferencia del rastro historico del reportante, aqui NO se emiten
  // features por segmento: este rastro se redibuja con cada posicion GPS y con
  // el tope de 500 puntos serian hasta 500 features extra por update. Las
  // flechas van sobre la linea completa con spacing fijo, que es barato y se
  // ve practicamente igual.
  safeAddLayer(map, {
    id: LAYERS.TRAIL_ARROWS_REPORTANT,
    type: 'symbol',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'LineString'],
      ['==', ['get', 'entityType'], 'reportant'],
    ],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 60,
      'text-field': '\u25B6',
      'text-size': 11,
      'text-keep-upright': false,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': ['coalesce', ['get', 'trailColor'], alertWarningColor],
      'text-halo-color': '#000',
      'text-halo-width': 1,
      'text-opacity': 0.75,
    },
  })
  safeAddLayer(map, {
    id: LAYERS.TRAIL_DOTS_REPORTANT,
    type: 'circle',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'Point'],
      ['==', ['get', 'entityType'], 'reportant'],
    ],
    paint: {
      'circle-radius': 3,
      'circle-color': ['coalesce', ['get', 'trailColor'], alertWarningColor],
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 1,
    },
  })

  // Device follow trail — §6.3: mantener comportamiento actual.
  // Filter excluye sos_call y reportant (ambos tienen layers dedicadas);
  // cualquier otro entityType cae acá — es el default operacional.
  safeAddLayer(map, {
    id: LAYERS.TRAIL_LINE,
    type: 'line',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'LineString'],
      ['!=', ['get', 'entityType'], 'sos_call'],
      ['!=', ['get', 'entityType'], 'reportant'],
    ],
    paint: {
      'line-color': ['coalesce', ['get', 'trailColor'], trailLineColor],
      'line-width': 2.5,
      'line-dasharray': [3, 2],
      'line-opacity': 0.8,
    },
  })

  // Flechas de direccion sobre el rastro de la unidad seguida (ECO). Mismo patron que
  // TRAIL_ARROWS_MC, que es el unico que las tenia.
  //
  // 'text-keep-upright': false es lo que hace que funcionen: por omision
  // MapLibre voltea los glifos de un symbol sobre linea para que nunca se lean
  // de cabeza, y con una flecha eso significa invertirla en cada tramo que va
  // hacia el poniente. Un rastro que miente sobre su direccion es peor que uno
  // que no la muestra.
  //
  // A diferencia del rastro historico del reportante, aqui NO se emiten
  // features por segmento: este rastro se redibuja con cada posicion GPS y con
  // el tope de 500 puntos serian hasta 500 features extra por update. Las
  // flechas van sobre la linea completa con spacing fijo, que es barato y se
  // ve practicamente igual.
  safeAddLayer(map, {
    id: LAYERS.TRAIL_ARROWS,
    type: 'symbol',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'LineString'],
      ['!=', ['get', 'entityType'], 'sos_call'],
      ['!=', ['get', 'entityType'], 'reportant'],
    ],
    layout: {
      'symbol-placement': 'line',
      'symbol-spacing': 60,
      'text-field': '\u25B6',
      'text-size': 11,
      'text-keep-upright': false,
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': ['coalesce', ['get', 'trailColor'], trailLineColor],
      'text-halo-color': '#000',
      'text-halo-width': 1,
      'text-opacity': 0.9,
    },
  })
  safeAddLayer(map, {
    id: LAYERS.TRAIL_DOTS,
    type: 'circle',
    source: TRAIL_SOURCE,
    filter: ['all',
      ['==', ['geometry-type'], 'Point'],
      ['!=', ['get', 'entityType'], 'sos_call'],
      ['!=', ['get', 'entityType'], 'reportant'],
    ],
    paint: {
      'circle-radius': 4,
      'circle-color': ['coalesce', ['get', 'trailColor'], trailLineColor],
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 1,
    },
  })
}

// Prefijos de layers propias — no queremos tocar nuestras paint props.
var OUR_LAYERS_RE = /^(unified-|jurisdiction-|trail-|geofence-|cluster-|heatmap-)/

/**
 * Atenúa los labels de símbolo del style base (ciudades, municipios, pois)
 * que caen FUERA del polígono del EdoMex. Usa el operador `within` de
 * MapLibre para hacer el check server-side por layer paint property.
 *
 * @param {Object} map - MapLibre map instance.
 * @param {boolean} enabled - Si true, atenúa labels externos al 25%;
 *                             si false, resetea a opacity 1 (default del
 *                             style).
 */
export function attenuateExternalLabels(map, enabled) {
  if (!map) return
  if (enabled === undefined) enabled = true
  var style = map.getStyle && map.getStyle()
  if (!style || !Array.isArray(style.layers)) return

  var withinEdomex = ['within', EDOMEX_BOUNDARY_GEOJSON.geometry]
  var opacityExpr = enabled ? ['case', withinEdomex, 1.0, 0.25] : 1

  for (var i = 0; i < style.layers.length; i++) {
    var layer = style.layers[i]
    if (layer.type !== 'symbol') continue
    if (OUR_LAYERS_RE.test(layer.id)) continue
    var hasText = layer.layout && layer.layout['text-field']
    if (!hasText) continue
    try {
      map.setPaintProperty(layer.id, 'text-opacity', opacityExpr)
    } catch (e) { /* skip */ }
  }
}

// NOTA: `hideBaseBuildingLayers` se eliminó. Antes ocultaba los edificios del
// style base porque saturaban el mapa, pero el C5 necesita ver footprints a
// zoom profundo para orientar. Ahora los estilos self-hosteados (dark/light)
// muestran los edificios con reveal por zoom + opacidad sutil (definido en
// scripts/tiles-styles-build.mjs → graftDetailLayers); 'streets' los trae del
// style base. Ya no hace falta tocarlos desde la app.

/**
 * Toggle de visibilidad de las 3 layers de jurisdicción (mask externo +
 * glow + línea nítida). Permite al operador apagar el remarcado si le
 * estorba. Usa setLayoutProperty 'visibility'.
 */
export function setJurisdictionVisible(map, visible) {
  if (!map) return
  var v = visible ? 'visible' : 'none'
  var ids = [LAYERS.JURISDICTION_MASK, LAYERS.JURISDICTION_LINE_GLOW, LAYERS.JURISDICTION_LINE]
  for (var i = 0; i < ids.length; i++) {
    if (map.getLayer(ids[i])) map.setLayoutProperty(ids[i], 'visibility', v)
  }
}

export function setLayerVisibility(map, layerId, visible) {
  if (map.getLayer(layerId)) {
    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none')
  }
}

/**
 * Sincroniza la fuente dedicada del heatmap con los incidentes de las
 * últimas 24 h, iterando el hash de monitores (pasado por el caller).
 * Hay que re-sincronizar cada vez que:
 *   - Se activa el toggle del heatmap.
 *   - Se completa el initial load (llegan incidentes nuevos).
 *   - Periódicamente (cada 5 min) mientras está visible, para que el
 *     cutoff de 24 h se mantenga deslizante.
 */
export function syncHeatmapIncidents(map, monitors) {
  if (!map) return
  var src = map.getSource(HEATMAP_INCIDENTS_SOURCE)
  if (!src) return
  var cutoff = Date.now() - 24 * 60 * 60 * 1000
  var features = []
  if (monitors) {
    var ids = Object.keys(monitors)
    for (var i = 0; i < ids.length; i++) {
      var m = monitors[ids[i]]
      if (!m || m.entityType !== 'incident') continue
      if (!m.coordinates) continue
      var raw = m.data || {}
      var ts = raw.createdAt || raw.serverTime || raw.startDate
      var tsMs = ts ? Date.parse(ts) : 0
      if (!tsMs || tsMs < cutoff) continue
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: m.coordinates },
        properties: { createdAtMs: tsMs },
      })
    }
  }
  src.setData({ type: 'FeatureCollection', features: features })
}

